// src/pages/UseCasePage.tsx
import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import { client } from "../sanity/client";
import { urlFor } from "../sanity/image";
import Title from "../components/typography/Title";
import Subtitle from "../components/typography/Subtitle";
import Description from "../components/typography/Description";
import Button from "../shared/button/Button";

// ─── Media types & helpers (shared with ProductPage) ────────────────────────
type SanityImage = {
  alt?: string;
  asset?: {
    url?: string;
    metadata?: { dimensions?: { width?: number; height?: number } };
  };
};

type SanityFile = {
  asset?: {
    url?: string;
  };
};

type MediaBlock = {
  kind?: "image" | "videoFile" | "audioFile" | "embedUrl" | "gifUrl" | "icon";
  title?: string;
  caption?: string;
  alt?: string;
  image?: SanityImage | any;
  posterImage?: SanityImage | any;
  videoFile?: SanityFile | null;
  audioFile?: SanityFile | null;
  embedUrl?: string;
  gifUrl?: string;
  iconName?: string;
  autoplay?: boolean;
  loop?: boolean;
  muted?: boolean;
};

function getMediaImageUrl(media: MediaBlock, width?: number): string | null {
  if (media.kind === "image" && (media as any)?.image?.asset?.url) {
    const base = (media as any).image.asset.url;
    if (width && urlFor) {
      try {
        return urlFor(base).width(width).url();
      } catch {
        return base;
      }
    }
    return base;
  }
  return null;
}

function MediaRender({
  media,
  className,
  style,
}: {
  media: MediaBlock;
  className?: string;
  style?: React.CSSProperties;
}) {
  if (media.kind === "image") {
    const url = getMediaImageUrl(media, 1200);
    if (!url) return null;
    return (
      <img
        src={url}
        alt={media.alt || media.title || ""}
        className={className || "h-auto w-full"}
        style={{ objectFit: "contain", ...style }}
        loading="lazy"
      />
    );
  }
  if (media.kind === "videoFile" && media.videoFile?.asset?.url) {
    const posterUrl = media.posterImage
      ? getMediaImageUrl(
          { ...media, kind: "image", image: media.posterImage },
          1200
        )
      : null;
    return (
      <video
        src={media.videoFile.asset.url}
        poster={posterUrl || undefined}
        controls={!media.autoplay}
        autoPlay={media.autoplay}
        loop={media.loop}
        muted={media.muted}
        className="h-auto w-full"
      />
    );
  }
  if (media.kind === "embedUrl" && media.embedUrl) {
    return (
      <div className="relative w-full pb-[56.25%]">
        <iframe
          src={media.embedUrl}
          className="absolute inset-0 h-full w-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }
  if (media.kind === "gifUrl" && media.gifUrl) {
    return (
      <img
        src={media.gifUrl}
        alt={media.alt || media.title || ""}
        className="h-auto w-full"
        loading="lazy"
      />
    );
  }
  if (media.kind === "audioFile" && media.audioFile?.asset?.url) {
    return (
      <div style={{ width: "100%" }}>
        <audio
          src={media.audioFile.asset.url}
          controls
          style={{ width: "100%", height: "48px" }}
        >
          Your browser does not support the audio element.
        </audio>
        {media.title && (
          <div
            style={{
              marginTop: "0.75rem",
              fontSize: "0.85rem",
              color: "rgba(255, 255, 255, 0.7)",
              textAlign: "center",
            }}
          >
            {media.title}
          </div>
        )}
      </div>
    );
  }
  return null;
}

type Accent = {
  pageBg: string;
  panelBg: string;
  accent: string;
  accA: string;
  accB: string;
  glow: string;
  border: string;
};

function getAccentByUseCaseSlug(slug?: string): Accent {
  const key = (slug || "").toLowerCase();

  const palette: Record<string, Accent> = {
    "universal-ai-recruiter": {
      pageBg: "#0A1810",
      panelBg: "#0F241B",
      accent: "#14AE5C",
      accA: "#14AE5C",
      accB: "#148F4E",
      glow: "rgba(20,174,92,0.35)",
      border: "rgba(20,174,92,0.22)",
    },
    "technical-ai-recruiter": {
      pageBg: "#071C22",
      panelBg: "#0A2630",
      accent: "#00E5FF",
      accA: "#00E5FF",
      accB: "#00B4D8",
      glow: "rgba(0,229,255,0.35)",
      border: "rgba(0,229,255,0.22)",
    },
    "ai-campus-drives": {
      pageBg: "#121027",
      panelBg: "#19163A",
      accent: "#7AA7FF",
      accA: "#7AA7FF",
      accB: "#5182E8",
      glow: "rgba(122,167,255,0.35)",
      border: "rgba(122,167,255,0.22)",
    },
    "high-volume-recruiter": {
      pageBg: "#1A0A20",
      panelBg: "#2A1431",
      accent: "#FF6A00",
      accA: "#FF6A00",
      accB: "#D95700",
      glow: "rgba(255,106,0,0.35)",
      border: "rgba(255,106,0,0.22)",
    },
    "diversity-hiring": {
      pageBg: "#1A0F20",
      panelBg: "#251631",
      accent: "#C77DFF",
      accA: "#C77DFF",
      accB: "#9D4EDD",
      glow: "rgba(199,125,255,0.35)",
      border: "rgba(199,125,255,0.22)",
    },
    "job-fests": {
      pageBg: "#181208",
      panelBg: "#241A0F",
      accent: "#FDBB30",
      accA: "#FDBB30",
      accB: "#E5A000",
      glow: "rgba(253,187,48,0.35)",
      border: "rgba(253,187,48,0.22)",
    },
    "exit-interviews": {
      pageBg: "#1C0808",
      panelBg: "#2A0F0F",
      accent: "#FF3D6E",
      accA: "#FF3D6E",
      accB: "#D42B56",
      glow: "rgba(255,61,110,0.35)",
      border: "rgba(255,61,110,0.22)",
    },
    "internal-employee-surveys": {
      pageBg: "#081B14",
      panelBg: "#0B241B",
      accent: "#25FF7A",
      accA: "#25FF7A",
      accB: "#1DD968",
      glow: "rgba(37,255,122,0.35)",
      border: "rgba(37,255,122,0.22)",
    },
  };

  return (
    palette[key] || {
      pageBg: "#0A1810",
      panelBg: "#0F241B",
      accent: "#14AE5C",
      accA: "#14AE5C",
      accB: "#148F4E",
      glow: "rgba(20,174,92,0.35)",
      border: "rgba(20,174,92,0.22)",
    }
  );
}

function accentVars(accent: Accent) {
  return {
    "--pageBg": accent.pageBg,
    "--panelBg": accent.panelBg,
    "--acc": accent.accent,
    "--accA": accent.accA,
    "--accB": accent.accB,
    "--accGlow": accent.glow,
    "--accBorder": accent.border,
  } as React.CSSProperties;
}

function solidHeroBg(): React.CSSProperties {
  return {
    backgroundColor: "var(--pageBg)",
    backgroundImage: `
      radial-gradient(900px 520px at 18% 28%, var(--accGlow), transparent 60%),
      radial-gradient(760px 420px at 82% 72%, rgba(255,255,255,0.07), transparent 62%)
    `,
  } as React.CSSProperties;
}

function solidCtaBg(): React.CSSProperties {
  return {
    backgroundColor: "var(--pageBg)",
    backgroundImage: `
      radial-gradient(1000px 560px at 20% 40%, var(--accGlow), transparent 62%),
      radial-gradient(820px 520px at 85% 65%, rgba(255,255,255,0.08), transparent 66%)
    `,
  } as React.CSSProperties;
}

const USE_CASE_BY_SLUG = `
*[_type == "useCasePage" && slug.current == $slug][0]{
  useCaseName,
  category,

  hero{
    category,
    headline,
    subheadline,
    heroVisual{
      kind,
      title,
      caption,
      alt,
      image{asset->{url, metadata{dimensions}}},
      embedUrl,
      gifUrl,
      iconName
    },
    ctas[]{label, href, variant}
  },

  sections[]{
    _type,
    enabled,
    heading,
    subheading,
    overview,
    intro,
    anchor,

    diagramHeading,
    diagramImage{asset->{url, metadata{dimensions}}},
    diagramImageAlt,

    steps[]{
      stepNumber,
      icon{asset->{url, metadata{dimensions}}},
      title,
      description,
      isAutomated,
      automationDetail,
      timeSaved,
      media{
        kind,
        title,
        caption,
        alt,
        image{asset->{url, metadata{dimensions}}},
        posterImage{asset->{url, metadata{dimensions}}},
        videoFile{asset->{url}},
        audioFile{asset->{url}},
        embedUrl,
        gifUrl,
        iconName,
        autoplay,
        loop,
        muted
      }
    },

    comparisons[]{
      label,
      traditional,
      withAI,
      percentageSaved,
      breakdown[]{
        phase,
        traditional,
        withAI
      }
    },

    useCases[]{
      title,
      scenario,
      outcome
    },

    testimonials[]{
      quote,
      author,
      role,
      company,
      hiringDetails,
      logo{kind, image{asset->{url, metadata{dimensions}}, alt}}
    },

    features[]{
      name,
      icon
    },

    carouselImages[]{
      image{asset->{url, metadata{dimensions}}},
      alt,
      caption
    },

    items[]{
      title,
      description,
      media{
        kind,
        title,
        caption,
        alt,
        image{asset->{url, metadata{dimensions}}},
        posterImage{asset->{url, metadata{dimensions}}},
        videoFile{asset->{url}},
        audioFile{asset->{url}},
        embedUrl,
        gifUrl,
        iconName,
        autoplay,
        loop,
        muted
      }
    },

    title,
    description,
    image{asset->{url, metadata{dimensions}}},
    imageAlt,
    ctaLabel,
    ctaHref,

    primaryCta{label, href},
    secondaryCta{label, href}
  },

  tabTitle,
  seoTitle,
  seoDescription,
  seoKeywords,
  seoImage{asset->{url}},
  canonicalUrl,
  noIndex,

  slug
}
`;

// Products & Features Section Component
const ProductsFeaturesSection: React.FC<{ section: any }> = ({ section }) => {
  // Feature descriptions mapping
  const getFeatureDescription = (name: string): string => {
    const descriptions: Record<string, string> = {
      "JD+CV Matching":
        "Intelligent resume screening and job description matching with AI-powered candidate ranking",
      "AI Interviews on Video":
        "Conduct automated video interviews with natural language processing and sentiment analysis",
      "AI Phone Screens":
        "Voice-based AI screening with real-time conversation and candidate evaluation",
      "Code Assessments":
        "Advanced coding challenges with automated evaluation and plagiarism detection",
      "AI-Proctoring":
        "Secure assessment monitoring with AI-powered fraud detection and integrity verification",
    };
    return descriptions[name] || "Advanced AI-powered recruitment capability";
  };

  // Icon mapping for features
  const getFeatureIcon = (name: string): React.ReactNode => {
    const iconMap: Record<string, React.ReactNode> = {
      "JD+CV Matching": (
        <svg
          className="h-7 w-7"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      ),
      "AI Interviews on Video": (
        <svg
          className="h-7 w-7"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
          />
        </svg>
      ),
      "AI Phone Screens": (
        <svg
          className="h-7 w-7"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
          />
        </svg>
      ),
      "Code Assessments": (
        <svg
          className="h-7 w-7"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
          />
        </svg>
      ),
      "AI-Proctoring": (
        <svg
          className="h-7 w-7"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
          />
        </svg>
      ),
    };
    return (
      iconMap[name] || (
        <svg
          className="h-7 w-7"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 13l4 4L19 7"
          />
        </svg>
      )
    );
  };

  // Carousel state
  const [activeSlide, setActiveSlide] = useState(0);
  const [selectedFeature, setSelectedFeature] = useState<string | null>(null);
  const carouselRef = React.useRef<HTMLDivElement>(null);
  const hasImages = section.carouselImages && section.carouselImages.length > 0;
  const totalSlides = hasImages ? section.carouselImages.length : 0;

  // Set initial selected feature
  useEffect(() => {
    if (section.features && section.features.length > 0 && !selectedFeature) {
      setSelectedFeature(section.features[0].name);
    }
  }, [section.features, selectedFeature]);

  // Handle feature click
  const handleFeatureClick = (featureName: string, index: number) => {
    setSelectedFeature(featureName);
    if (hasImages && index < totalSlides) {
      setActiveSlide(index);
      // Smooth scroll to carousel
      setTimeout(() => {
        carouselRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  };

  // Handle carousel navigation (arrows/dots) - update selected feature
  const handleSlideChange = (newIndex: number) => {
    setActiveSlide(newIndex);
    if (section.features && section.features[newIndex]) {
      setSelectedFeature(section.features[newIndex].name);
    }
  };

  return (
    <section className="relative overflow-hidden px-4 py-20 md:px-12 md:py-32">
      {/* Background decorations */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-gray-900/30 via-transparent to-gray-900/30"></div>
      <div className="bg-[var(--acc)]/5 absolute left-0 top-1/4 h-96 w-96 rounded-full blur-3xl"></div>
      <div className="bg-[var(--acc)]/5 absolute bottom-1/4 right-0 h-96 w-96 rounded-full blur-3xl"></div>

      <div className="relative mx-auto max-w-7xl">
        {/* Section Header */}
        <div className="mb-20 text-center">
          <Title size="xl" className="mb-6">
            {section.heading}
          </Title>
          <Description className="mx-auto max-w-3xl text-xl leading-relaxed text-gray-400">
            Comprehensive suite of AI-powered tools designed to revolutionize
            your technical hiring process from start to finish
          </Description>
        </div>

        {/* Features Grid */}
        <div className="mb-20 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {section.features.map(
            (feature: any, idx: number) =>
              feature.name && (
                <div
                  key={idx}
                  onClick={() => handleFeatureClick(feature.name, idx)}
                  className={`hover:border-[var(--acc)]/40 hover:shadow-[var(--acc)]/10 group relative cursor-pointer rounded-2xl border bg-gradient-to-br from-gray-800/40 via-gray-800/20 to-gray-900/40 p-8 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${
                    selectedFeature === feature.name
                      ? "shadow-[var(--acc)]/20 border-[var(--acc)] shadow-2xl"
                      : "border-gray-700/50"
                  }`}
                >
                  {/* Glow effect on hover */}
                  <div
                    className={`from-[var(--acc)]/0 to-[var(--acc)]/0 group-hover:from-[var(--acc)]/5 absolute inset-0 rounded-2xl bg-gradient-to-br transition-all duration-300 group-hover:to-transparent ${
                      selectedFeature === feature.name
                        ? "from-[var(--acc)]/10"
                        : ""
                    }`}
                  ></div>

                  {/* Selected indicator */}
                  {selectedFeature === feature.name && (
                    <div className="shadow-[var(--acc)]/50 absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-[var(--acc)] shadow-lg">
                      <svg
                        className="h-4 w-4 text-gray-900"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                  )}

                  <div className="relative">
                    {/* Icon */}
                    <div className="mb-6">
                      <div
                        className={`from-[var(--acc)]/20 via-[var(--acc)]/10 border-[var(--acc)]/30 group-hover:border-[var(--acc)]/50 inline-flex h-16 w-16 items-center justify-center rounded-2xl border bg-gradient-to-br to-transparent backdrop-blur-sm transition-all duration-300 group-hover:scale-110 ${
                          selectedFeature === feature.name
                            ? "border-[var(--acc)]/50 scale-110"
                            : ""
                        }`}
                      >
                        <span
                          className={`text-[var(--acc)] group-hover:drop-shadow-[0_0_8px_var(--acc)] ${
                            selectedFeature === feature.name
                              ? "drop-shadow-[0_0_8px_var(--acc)]"
                              : ""
                          }`}
                        >
                          {getFeatureIcon(feature.name)}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <h3
                      className={`mb-3 font-serif text-2xl font-light transition-colors duration-300 group-hover:text-[var(--acc)] ${
                        selectedFeature === feature.name
                          ? "text-[var(--acc)]"
                          : "text-white"
                      }`}
                    >
                      {feature.name}
                    </h3>
                    <p className="text-sm leading-relaxed text-gray-400 transition-colors group-hover:text-gray-300">
                      {getFeatureDescription(feature.name)}
                    </p>
                  </div>
                </div>
              )
          )}
        </div>

        {/* Image Carousel */}
        {hasImages && (
          <div ref={carouselRef} className="mx-auto max-w-5xl scroll-mt-8">
            <div className="mb-8 text-center">
              <h3 className="mb-2 text-2xl font-light text-gray-300">
                See It In Action
              </h3>
              <p className="text-gray-500">
                Real screenshots from our platform
              </p>
            </div>

            <div className="">
              {/* Carousel Container */}
              <div className="inline-flex items-center gap-2 rounded-t-xl border border-b-0 border-app-900 bg-app-900 px-6 py-3">
                <div className="h-2 w-2 animate-pulse rounded-full bg-[var(--acc)]"></div>
                <p className="font-semibold text-white">
                  {selectedFeature || "Select a feature"}
                </p>
              </div>
              <div className="relative overflow-hidden">
                <div
                  className="flex transition-transform duration-700 ease-in-out"
                  style={{ transform: `translateX(-${activeSlide * 100}%)` }}
                >
                  {section.carouselImages.map((img: any, idx: number) => (
                    <div key={idx} className="min-w-full">
                      <div className="relative aspect-video border-8 border-app-900 bg-gray-900">
                        <img
                          src={img.image.asset.url}
                          alt={img.alt || `Product feature ${idx + 1}`}
                          className="h-full w-full object-contain"
                        />
                      </div>
                      {img.caption && (
                        <div className="bg-gradient-to-t from-gray-900 via-gray-900/95 to-transparent p-6">
                          <p className="text-center text-base text-gray-300">
                            {img.caption}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Carousel Controls */}
              {totalSlides > 1 && (
                <div className="mt-8 flex items-center justify-center gap-6">
                  {/* Previous Arrow */}
                  <button
                    onClick={() =>
                      handleSlideChange(
                        (activeSlide - 1 + totalSlides) % totalSlides
                      )
                    }
                    className="group flex h-12 w-12 items-center justify-center rounded-full border border-gray-700 bg-gray-900/90 text-white backdrop-blur-sm transition-all duration-300 hover:scale-110 hover:border-[var(--acc)] hover:bg-[var(--acc)]"
                    aria-label="Previous slide"
                  >
                    <svg
                      className="h-6 w-6 transition-transform group-hover:scale-110"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>

                  {/* Dots Indicator */}
                  <div className="flex justify-center gap-3">
                    {section.carouselImages.map((_: any, idx: number) => (
                      <button
                        key={idx}
                        onClick={() => handleSlideChange(idx)}
                        className="group relative"
                        aria-label={`Go to slide ${idx + 1}`}
                      >
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${
                            idx === activeSlide
                              ? "w-12 bg-[var(--acc)]"
                              : "w-2 bg-gray-600 hover:bg-gray-500"
                          }`}
                        >
                          {idx === activeSlide && (
                            <div className="absolute inset-0 rounded-full bg-[var(--acc)] opacity-50 blur-sm"></div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>

                  {/* Next Arrow */}
                  <button
                    onClick={() =>
                      handleSlideChange((activeSlide + 1) % totalSlides)
                    }
                    className="group flex h-12 w-12 items-center justify-center rounded-full border border-gray-700 bg-gray-900/90 text-white backdrop-blur-sm transition-all duration-300 hover:scale-110 hover:border-[var(--acc)] hover:bg-[var(--acc)]"
                    aria-label="Next slide"
                  >
                    <svg
                      className="h-6 w-6 transition-transform group-hover:scale-110"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

const UseCasePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [useCase, setUseCase] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;

    client
      .fetch(USE_CASE_BY_SLUG, { slug })
      .then((data) => {
        if (!data) {
          setError(`Use Case "${slug}" not found`);
        } else {
          setUseCase(data);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error loading use case data:", err);
        setError(`Use Case "${slug}" not found`);
        setLoading(false);
      });
  }, [slug]);

  const accent = getAccentByUseCaseSlug(slug);

  // Helper to get section by type
  const getSection = (type: string) => {
    if (!useCase?.sections) return null;
    return useCase.sections.find(
      (s: any) => s._type === type && s.enabled !== false
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-900">
        <div className="text-xl text-gray-300">Loading...</div>
      </div>
    );
  }

  if (error || !useCase) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="mb-4 text-2xl text-white">Use Case Not Found</div>
          <div className="text-gray-400">
            {error || "Unable to load use case"}
          </div>
        </div>
      </div>
    );
  }

  const pageTitle = useCase.tabTitle || useCase.useCaseName;
  const seoTitle = useCase.seoTitle || useCase.useCaseName;
  const seoDescription = useCase.seoDescription || useCase.hero.subheadline;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        {useCase.seoKeywords && (
          <meta name="keywords" content={useCase.seoKeywords.join(", ")} />
        )}
        {useCase.noIndex && <meta name="robots" content="noindex" />}
        {useCase.canonicalUrl && (
          <link rel="canonical" href={useCase.canonicalUrl} />
        )}
      </Helmet>

      <div
        style={accentVars(accent)}
        className="min-h-screen bg-[var(--pageBg)] text-white"
      >
        {/* Hero Section with Overview */}
        <section
          style={solidHeroBg()}
          className="px-4 py-16 md:px-12 md:py-24 lg:py-32"
        >
          <div className="mx-auto max-w-6xl">
            {/* Hero Content */}
            <div className="mb-10 text-center">
              <Subtitle className="mb-4 text-sm font-semibold uppercase tracking-wider text-[var(--acc)]">
                {useCase.hero.category} Use Case
              </Subtitle>
              <Title size="xl" className="mb-6">
                {useCase.hero.headline}
              </Title>
              <Description className="mx-auto mb-10 max-w-3xl text-xl leading-relaxed text-gray-300">
                {useCase.hero.subheadline}
              </Description>
            </div>

            {/* Overview - Side by side with How It Works for specific pages */}
            {(() => {
              const overviewSection = getSection("overviewSection");
              const howItWorksSection = getSection("howItWorks");
              const isSideBySide =
                (slug === "high-volume-recruiter" ||
                  slug === "diversity-hiring") &&
                overviewSection &&
                howItWorksSection?.steps?.length > 0;

              if (isSideBySide) {
                return (
                  <div className="mx-auto max-w-7xl">
                    <div className="grid items-start gap-8 lg:grid-cols-2">
                      {/* Left - Overview (sticky on scroll) */}
                      <div className="lg:sticky lg:top-24">
                        <div className="rounded-3xl border border-[var(--accBorder)] bg-gradient-to-br from-gray-900/50 via-gray-900/30 to-gray-900/50 p-8 shadow-2xl shadow-[var(--accGlow)] backdrop-blur-md md:p-10">
                          {/* Section label */}
                          <div className="mb-5 inline-flex items-center gap-2">
                            <div className="h-2 w-2 rounded-full bg-[var(--acc)]" />
                            <span
                              style={{
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                color: "var(--acc)",
                                textTransform: "uppercase",
                                letterSpacing: "0.12em",
                              }}
                            >
                              Overview
                            </span>
                          </div>

                          {overviewSection.heading && (
                            <h2
                              className="font-serif"
                              style={{
                                fontSize: "clamp(1.6rem, 2.5vw, 2rem)",
                                letterSpacing: "-0.02em",
                                lineHeight: 1.2,
                                color: "#ffffff",
                                marginBottom: "1.25rem",
                              }}
                            >
                              {overviewSection.heading}
                            </h2>
                          )}

                          {overviewSection.overview && (
                            <p
                              style={{
                                fontSize: "0.95rem",
                                color: "rgba(255,255,255,0.75)",
                                lineHeight: 1.8,
                                fontWeight: 300,
                                whiteSpace: "pre-line",
                              }}
                            >
                              {overviewSection.overview}
                            </p>
                          )}

                          {overviewSection.diagramImage && (
                            <div className="mt-8">
                              {overviewSection.diagramHeading && (
                                <p
                                  style={{
                                    fontSize: "0.8rem",
                                    fontWeight: 600,
                                    color: "var(--acc)",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.1em",
                                    marginBottom: "0.75rem",
                                  }}
                                >
                                  {overviewSection.diagramHeading}
                                </p>
                              )}
                              <div className="overflow-hidden rounded-xl border border-white/10">
                                <img
                                  src={overviewSection.diagramImage.asset.url}
                                  alt={
                                    overviewSection.diagramImageAlt ||
                                    overviewSection.diagramHeading ||
                                    "Use case diagram"
                                  }
                                  className="h-auto w-full"
                                />
                              </div>
                            </div>
                          )}

                          {/* CTA buttons inside overview */}
                          {/*useCase.hero.ctas && useCase.hero.ctas.length > 0 && (
                            <div className="mt-8 flex flex-wrap gap-3">
                              {useCase.hero.ctas.map((cta: any, idx: number) => (
                                <Link key={idx} to={cta.href || "#"}>
                                  <Button
                                    variant={cta.variant === "primary" ? "primary" : "secondary"}
                                    size="lg"
                                  >
                                    {cta.label}
                                  </Button>
                                </Link>
                              ))}
                            </div>
                          )*/}
                        </div>
                      </div>

                      {/* Right - How It Works Timeline */}
                      <div className="flex flex-col rounded-3xl border border-[var(--accBorder)] bg-gradient-to-br from-gray-900/50 via-gray-900/30 to-gray-900/50 p-8 shadow-2xl shadow-[var(--accGlow)] backdrop-blur-md md:p-10">
                        {howItWorksSection.heading && (
                          <h2
                            className="font-serif"
                            style={{
                              fontSize: "clamp(1.6rem, 2.5vw, 2rem)",
                              letterSpacing: "-0.02em",
                              lineHeight: 1.15,
                              color: "#ffffff",
                              marginBottom: "0.5rem",
                            }}
                          >
                            {howItWorksSection.heading}
                          </h2>
                        )}
                        {howItWorksSection.intro && (
                          <p
                            style={{
                              fontSize: "0.9rem",
                              color: "rgba(255,255,255,0.7)",
                              lineHeight: 1.7,
                              fontWeight: 300,
                              marginBottom: "2rem",
                            }}
                          >
                            {howItWorksSection.intro}
                          </p>
                        )}
                        {!howItWorksSection.intro && <div className="mb-8" />}

                        <div
                          style={{
                            position: "relative",
                            paddingLeft: "2.5rem",
                          }}
                        >
                          {/* Timeline Line */}
                          <div
                            style={{
                              position: "absolute",
                              left: "11px",
                              top: 0,
                              bottom: 0,
                              width: "2px",
                              background: `linear-gradient(to bottom, var(--acc), var(--accBorder))`,
                            }}
                          />

                          {howItWorksSection.steps.map(
                            (step: any, i: number) => (
                              <div
                                key={i}
                                style={{
                                  position: "relative",
                                  marginBottom:
                                    i === howItWorksSection.steps.length - 1
                                      ? 0
                                      : "2rem",
                                }}
                              >
                                {/* Timeline Dot */}
                                <div
                                  style={{
                                    position: "absolute",
                                    left: "-2.5rem",
                                    top: "0.35rem",
                                    width: "24px",
                                    height: "24px",
                                    background: "var(--panelBg)",
                                    border: "3px solid var(--acc)",
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxShadow: "0 0 0 3px var(--panelBg)",
                                    zIndex: 1,
                                  }}
                                >
                                  <div
                                    style={{
                                      width: "8px",
                                      height: "8px",
                                      background: "var(--acc)",
                                      borderRadius: "50%",
                                    }}
                                  />
                                </div>

                                <div
                                  style={{
                                    fontSize: "0.7rem",
                                    fontWeight: 600,
                                    color: "var(--acc)",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.1em",
                                    marginBottom: "0.25rem",
                                  }}
                                >
                                  Step {String(i + 1).padStart(2, "0")}
                                </div>
                                {step.title && (
                                  <h3
                                    className="font-serif"
                                    style={{
                                      fontSize: "1.25rem",
                                      letterSpacing: "-0.02em",
                                      marginBottom: "0.35rem",
                                      color: "#ffffff",
                                    }}
                                  >
                                    {step.title}
                                  </h3>
                                )}
                                {step.description && (
                                  <p
                                    style={{
                                      fontSize: "0.82rem",
                                      color: "rgba(255,255,255,0.7)",
                                      lineHeight: 1.6,
                                    }}
                                  >
                                    {step.description}
                                  </p>
                                )}
                                {step.media && (
                                  <div
                                    style={{
                                      marginTop: "0.75rem",
                                      borderRadius: "12px",
                                      overflow: "hidden",
                                      border:
                                        "1px solid rgba(255, 255, 255, 0.1)",
                                    }}
                                  >
                                    <MediaRender media={step.media} />
                                  </div>
                                )}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }

              // Default: overview only (for other use case pages)
              if (
                !overviewSection ||
                (!overviewSection.overview && !overviewSection.diagramImage)
              )
                return null;
              return (
                <div className="mx-auto max-w-5xl">
                  {/* Combined Overview + Diagram Card */}
                  <div className="rounded-3xl border border-[var(--accBorder)] bg-gray-900/30 p-10 shadow-2xl shadow-[var(--accGlow)] backdrop-blur-md md:p-14">
                    {/* Overview Text */}
                    {overviewSection.overview && (
                      <>
                        {overviewSection.heading && (
                          <Title
                            size="base"
                            className="mb-6 text-center text-[var(--acc)]"
                          >
                            {overviewSection.heading}
                          </Title>
                        )}
                        <Description className="mb-12 whitespace-pre-line text-xl leading-relaxed text-gray-200">
                          {overviewSection.overview}
                        </Description>
                      </>
                    )}

                    {/* Use Case Diagram - if provided */}
                    {overviewSection.diagramImage && (
                      <div>
                        {overviewSection.diagramHeading && (
                          <Title
                            size="base"
                            className="mb-6 text-center text-gray-300"
                          >
                            {overviewSection.diagramHeading}
                          </Title>
                        )}
                        <div className="border-[var(--accBorder)]/50 overflow-hidden rounded-xl border">
                          <img
                            src={overviewSection.diagramImage.asset.url}
                            alt={
                              overviewSection.diagramImageAlt ||
                              overviewSection.diagramHeading ||
                              "Use case diagram"
                            }
                            className="h-auto w-full"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {useCase.hero.ctas && useCase.hero.ctas.length > 0 && (
              <div className="mb-4 mt-10 flex flex-wrap justify-center gap-4">
                {useCase.hero.ctas.map((cta, idx) => (
                  <Link key={idx} to={cta.href || "#"}>
                    <Button
                      variant={
                        cta.variant === "primary" ? "primary" : "secondary"
                      }
                      size="lg"
                    >
                      {cta.label}
                    </Button>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Process Section - Grid-Based Flow */}
        {(() => {
          const section = getSection("processSection");
          if (!section?.steps || section.steps.length === 0) return null;
          return (
            <section className="px-4 py-16 md:px-12 md:py-20">
              <div className="mx-auto max-w-7xl">
                <Title size="lg" className="mb-12 text-center">
                  {section.heading || "How It Works"}
                </Title>
                <Description className="mx-auto mb-12 max-w-3xl text-center text-gray-400">
                  {section.subheading ||
                    "End-to-end automated workflow with AI handling the heavy lifting"}
                </Description>
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {section.steps.map((step: any, idx: number) => (
                    <div
                      key={idx}
                      className="hover:shadow-[var(--acc)]/10 group relative rounded-2xl border border-gray-550 bg-gradient-to-br from-gray-800 to-gray-900 p-6 transition-all hover:border-[var(--acc)] hover:shadow-lg"
                    >
                      {/* Step Number or Icon Badge (if provided) */}
                      {(step.stepNumber || step.icon) && (
                        <div className="bg-[var(--acc)]/10 border-[var(--acc)]/30 mb-4 inline-flex h-12 w-12 items-center justify-center overflow-hidden rounded-xl border">
                          {!step.stepNumber ? (
                            <img
                              src={step.icon.asset.url}
                              alt="Step icon"
                              className="h-full w-full object-contain"
                            />
                          ) : (
                            <span className="font-serif text-2xl font-bold text-[var(--acc)]">
                              {step.stepNumber}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Automation Badge */}
                      {step.isAutomated && (
                        <div className="absolute right-4 top-4">
                          <span className="bg-[var(--acc)]/20 inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold text-[var(--acc)]">
                            <span className="h-2 w-2 animate-pulse rounded-full bg-[var(--acc)]"></span>
                            AI
                          </span>
                        </div>
                      )}

                      {/* Content */}
                      <h4 className="mb-3 font-serif text-xl font-light text-white">
                        {step.title}
                      </h4>
                      {step.description && (
                        <p className="mb-4 text-sm leading-relaxed text-gray-300">
                          {step.description}
                        </p>
                      )}

                      {/* Time Saved */}
                      {step.timeSaved && (
                        <div className="mt-4 flex items-center gap-2 text-xs text-gray-400">
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          <span>
                            Saves{" "}
                            <span className="font-semibold text-[var(--acc)]">
                              {step.timeSaved}
                            </span>
                          </span>
                        </div>
                      )}

                      {/* Automation Detail */}
                      {step.automationDetail && (
                        <div className="mt-2 rounded-lg bg-gray-900/50 px-3 py-2 text-xs text-gray-450">
                          {step.automationDetail}
                        </div>
                      )}

                      {/* Arrow for flow indication (except last item and row ends) */}
                      {idx < section.steps.length - 1 && (
                        <div
                          className={`absolute -right-6 top-1/2 z-20 hidden h-6 w-6 -translate-y-1/2 transform ${(idx + 1) % 2 === 0 ? "md:hidden" : "md:block"} ${(idx + 1) % 3 === 0 ? "lg:hidden" : "lg:block"}`}
                        >
                          {/* <svg
                            className="h-8 w-8 text-[var(--acc)] drop-shadow-[0_0_8px_var(--acc)]"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg> */}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </section>
          );
        })()}

        {/* How It Works - Timeline Style (standalone, skipped for side-by-side pages) */}
        {(() => {
          if (
            slug === "high-volume-recruiter" ||
            slug === "diversity-hiring"
          )
            return null;
          const section = getSection("howItWorks");
          if (!section?.steps || section.steps.length === 0) return null;
          return (
            <section
              id={section.anchor || undefined}
              style={{ padding: "8rem 0" }}
              className="px-4 md:px-12"
            >
              {/* Centered Header */}
              <div
                style={{
                  textAlign: "center",
                  marginBottom: "4rem",
                  maxWidth: "800px",
                  margin: "0 auto 4rem",
                }}
              >
                {section.heading && (
                  <h2
                    className="section-title font-serif"
                    style={{
                      fontSize: "clamp(2.2rem, 3.5vw, 3rem)",
                      letterSpacing: "-0.02em",
                      lineHeight: 1.15,
                      color: "#ffffff",
                      marginBottom: "1rem",
                    }}
                  >
                    {section.heading}
                  </h2>
                )}
                {section.intro && (
                  <p
                    className="section-desc"
                    style={{
                      fontSize: "0.95rem",
                      color: "rgba(255,255,255,0.7)",
                      lineHeight: 1.7,
                      fontWeight: 300,
                    }}
                  >
                    {section.intro}
                  </p>
                )}
              </div>

              <div
                style={{
                  position: "relative",
                  paddingLeft: "2.5rem",
                  maxWidth: "900px",
                  margin: "0 auto",
                }}
              >
                {/* Timeline Line */}
                <div
                  style={{
                    content: "",
                    position: "absolute",
                    left: "11px",
                    top: 0,
                    bottom: 0,
                    width: "2px",
                    background: `linear-gradient(to bottom, var(--acc), var(--accBorder))`,
                  }}
                />

                {section.steps.map((step: any, i: number) => (
                  <div
                    key={i}
                    style={{
                      position: "relative",
                      marginBottom:
                        i === section.steps.length - 1 ? 0 : "2.8rem",
                    }}
                  >
                    {/* Timeline Dot */}
                    <div
                      style={{
                        position: "absolute",
                        left: "-2.5rem",
                        top: "0.35rem",
                        width: "24px",
                        height: "24px",
                        background: "var(--pageBg)",
                        border: `3px solid var(--acc)`,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: `0 0 0 3px var(--pageBg)`,
                        zIndex: 1,
                      }}
                    >
                      <div
                        style={{
                          width: "8px",
                          height: "8px",
                          background: "var(--acc)",
                          borderRadius: "50%",
                        }}
                      />
                    </div>

                    {/* Step Number */}
                    <div
                      style={{
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        color: "var(--acc)",
                        textTransform: "uppercase",
                        letterSpacing: "0.1em",
                        marginBottom: "0.25rem",
                      }}
                    >
                      Step {String(i + 1).padStart(2, "0")}
                    </div>
                    {/* Step Title */}
                    {step.title && (
                      <h3
                        className="font-serif"
                        style={{
                          fontSize: "1.45rem",
                          letterSpacing: "-0.02em",
                          marginBottom: "0.35rem",
                          color: "#ffffff",
                        }}
                      >
                        {step.title}
                      </h3>
                    )}

                    {/* Step Description */}
                    {step.description && (
                      <p
                        style={{
                          fontSize: "0.85rem",
                          color: "rgba(255,255,255,0.7)",
                          lineHeight: 1.6,
                          maxWidth: "380px",
                        }}
                      >
                        {step.description}
                      </p>
                    )}

                    {/* Step Media (if provided) */}
                    {step.media && (
                      <div
                        style={{
                          marginTop: "1rem",
                          borderRadius: "16px",
                          overflow: "hidden",
                          border: "1px solid rgba(255, 255, 255, 0.1)",
                          maxWidth: "500px",
                        }}
                      >
                        <MediaRender media={step.media} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          );
        })()}

        {/* Time Savings Section */}
        {(() => {
          const section = getSection("timeSavingsSection");
          if (!section?.comparisons || section.comparisons.length === 0)
            return null;
          return (
            <section className="bg-gradient-to-b from-gray-900/30 to-transparent px-4 py-16 md:px-12 md:py-24">
              <div className="mx-auto max-w-6xl">
                <Title size="lg" className="mb-16 text-center">
                  {section.heading || "Numbers do the talking"}
                </Title>

                {/* Single Unified Card */}
                <div className="relative overflow-hidden rounded-3xl border border-[var(--accBorder)] bg-gradient-to-br from-gray-900/80 via-gray-900/60 to-gray-900/80 p-8 shadow-2xl shadow-[var(--accGlow)] backdrop-blur-xl md:p-12">
                  {/* Background decoration */}
                  <div className="from-[var(--acc)]/5 to-[var(--acc)]/5 absolute inset-0 bg-gradient-to-r via-transparent"></div>

                  <div className="relative space-y-12">
                    {section.comparisons.map(
                      (comparison: any, compIdx: number) => (
                        <div key={compIdx}>
                          {/* Comparison Label */}
                          {comparison.label && (
                            <h3 className="mb-8 text-center font-serif text-2xl font-light text-[var(--acc)] md:text-3xl">
                              {comparison.label}
                            </h3>
                          )}

                          {/* Comparison Content */}
                          {(comparison.traditional || comparison.withAI) && (
                            <div>
                              <div className="mb-8 grid items-center gap-8 md:grid-cols-3">
                                {comparison.traditional && (
                                  <div className="text-center">
                                    <div className="mb-3 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gray-400">
                                      <svg
                                        className="h-4 w-4"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                      </svg>
                                      Traditional Process
                                    </div>
                                    <div className="font-serif text-xl font-light leading-tight text-gray-300 md:text-2xl lg:text-3xl">
                                      {comparison.traditional}
                                    </div>
                                  </div>
                                )}
                                {comparison.traditional &&
                                  comparison.withAI && (
                                    <div className="flex items-center justify-center">
                                      <div className="group relative">
                                        <div className="absolute inset-0 rounded-full bg-[var(--acc)] opacity-40 blur-xl"></div>
                                        <div className="shadow-[var(--acc)]/40 relative flex h-12 w-12 transform items-center justify-center rounded-full bg-gradient-to-br from-[var(--accA)] to-[var(--accB)] shadow-lg md:h-14 md:w-14">
                                          <svg
                                            className="h-6 w-6 text-gray-900 md:h-7 md:w-7"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={2.5}
                                              d="M13 7l5 5m0 0l-5 5m5-5H6"
                                            />
                                          </svg>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                {comparison.withAI && (
                                  <div className="text-center">
                                    <div className="mb-3 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[var(--acc)]">
                                      <svg
                                        className="h-4 w-4"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M13 10V3L4 14h7v7l9-11h-7z"
                                        />
                                      </svg>
                                      With AI Use Case
                                    </div>
                                    <div className="font-serif text-xl font-light leading-tight text-[var(--acc)] md:text-2xl lg:text-3xl">
                                      {comparison.withAI}
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Percentage Badge */}
                              {comparison.percentageSaved && (
                                <div className="text-center">
                                  <div className="bg-[var(--acc)]/10 border-[var(--acc)]/30 inline-flex items-center gap-2 rounded-xl border px-5 py-2 backdrop-blur-sm">
                                    <svg
                                      className="h-5 w-5 text-[var(--acc)]"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M5 10l7-7m0 0l7 7m-7-7v18"
                                      />
                                    </svg>
                                    <span className="font-serif text-2xl font-light text-[var(--acc)] md:text-3xl">
                                      {comparison.percentageSaved}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Breakdown (if provided) */}
                          {comparison.breakdown &&
                            comparison.breakdown.length > 0 && (
                              <div className="mt-6 grid gap-3 md:grid-cols-2">
                                {comparison.breakdown.map(
                                  (item: any, idx: number) => (
                                    <div
                                      key={idx}
                                      className="hover:border-[var(--acc)]/40 rounded-xl border border-gray-700/50 bg-gray-800/30 p-4 transition-all"
                                    >
                                      <div className="mb-2 text-sm font-semibold text-white">
                                        {item.phase}
                                      </div>
                                      <div className="flex items-center gap-2 text-xs">
                                        <span className="flex-1 text-gray-400">
                                          {item.traditional}
                                        </span>
                                        <svg
                                          className="h-3 w-3 flex-shrink-0 text-[var(--acc)]"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                          stroke="currentColor"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 5l7 7-7 7"
                                          />
                                        </svg>
                                        <span className="flex-1 font-medium text-[var(--acc)]">
                                          {item.withAI}
                                        </span>
                                      </div>
                                    </div>
                                  )
                                )}
                              </div>
                            )}

                          {/* Separator between comparisons (not after last one) */}
                          {compIdx < section.comparisons.length - 1 && (
                            <div className="border-[var(--accBorder)]/30 mt-12 border-t"></div>
                          )}
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            </section>
          );
        })()}

        {/* Use Cases Section */}
        {(() => {
          const section = getSection("useCasesSection");
          if (!section?.useCases || section.useCases.length === 0) return null;
          return (
            <section className="px-4 py-16 md:px-12 md:py-20">
              <div className="mx-auto max-w-6xl">
                <Title size="lg" className="mb-12 text-center">
                  {section.heading || "Real-World Use Cases"}
                </Title>
                <div className="grid gap-8 lg:grid-cols-3">
                  {section.useCases.map((useCase: any, idx: number) => (
                    <div
                      key={idx}
                      className="rounded-xl border border-gray-550 bg-gray-800 p-6"
                    >
                      <h4 className="mb-3 font-serif text-xl font-light">
                        {useCase.title}
                      </h4>
                      <div className="mb-4 text-sm text-gray-350">
                        {useCase.scenario && (
                          <div className="mb-2">
                            <span className="font-medium text-gray-300">
                              Scenario:
                            </span>{" "}
                            {useCase.scenario}
                          </div>
                        )}
                        {useCase.outcome && (
                          <div>
                            <span className="font-medium text-[var(--acc)]">
                              Outcome:
                            </span>{" "}
                            {useCase.outcome}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          );
        })()}

        {/* Testimonials Section */}
        {(() => {
          const section = getSection("useCaseTestimonialsSection");
          if (!section?.testimonials || section.testimonials.length === 0)
            return null;

          const testimonialCount = section.testimonials.length;

          // Dynamic grid classes based on testimonial count
          const gridClasses =
            testimonialCount === 1
              ? "flex justify-center"
              : testimonialCount === 2
                ? "grid gap-8 md:grid-cols-2 justify-items-center"
                : "grid gap-8 md:grid-cols-2 lg:grid-cols-3";

          return (
            <section className="px-4 py-16 md:px-12 md:py-20">
              <div className="mx-auto max-w-6xl">
                <Title size="lg" className="mb-12 text-center">
                  {section.heading || "Success Stories"}
                </Title>
                <div className={gridClasses}>
                  {section.testimonials.map((testimonial: any, idx: number) => (
                    <div
                      key={idx}
                      className={`flex flex-col rounded-xl border border-gray-550 bg-gray-800 p-8 ${
                        testimonialCount <= 2 ? "w-full max-w-md" : ""
                      }`}
                    >
                      {/* Company Header with Logo */}
                      {(testimonial.company || testimonial.logo) && (
                        <div className="mb-4 flex items-center gap-4">
                          {testimonial.logo?.image?.asset?.url && (
                            <div className="h-12 w-12 flex-shrink-0 overflow-hidden p-2">
                              <img
                                src={testimonial.logo.image.asset.url}
                                alt={
                                  testimonial.logo.alt ||
                                  `${testimonial.company} logo`
                                }
                                className="h-full w-full object-contain"
                              />
                            </div>
                          )}
                          {testimonial.company && (
                            <div className="text-xl font-bold text-[var(--acc)]">
                              {testimonial.company}
                            </div>
                          )}
                        </div>
                      )}

                      {testimonial.hiringDetails && (
                        <div className="mb-4 text-sm italic text-gray-400">
                          {testimonial.hiringDetails}
                        </div>
                      )}
                      {testimonial.quote && (
                        <p className="mb-6 flex-grow text-base italic leading-relaxed text-gray-200">
                          "{testimonial.quote}"
                        </p>
                      )}
                      {(testimonial.author || testimonial.role) && (
                        <div className="mt-auto border-t border-gray-700 pt-4">
                          {testimonial.author && (
                            <div className="font-medium text-white">
                              {testimonial.author}
                            </div>
                          )}
                          {testimonial.role && (
                            <div className="text-sm text-gray-400">
                              {testimonial.role}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </section>
          );
        })()}

        {/* Case Study Section */}
        {(() => {
          const section = getSection("caseStudySection");
          if (!section?.title) return null;
          const csImageUrl = section.image?.asset?.url || null;
          return (
            <section className="px-4 py-16 md:px-12 md:py-24">
              <div className="mx-auto max-w-5xl">
                {section.heading && (
                  <Title size="lg" className="mb-12 text-center">
                    {section.heading}
                  </Title>
                )}

                <div className="relative overflow-hidden rounded-3xl border border-[var(--accBorder)] bg-gradient-to-br from-gray-900/80 via-gray-900/60 to-gray-900/80 shadow-2xl shadow-[var(--accGlow)] backdrop-blur-xl">
                  {/* Accent stripe */}
                  <div className="h-1 w-full bg-gradient-to-r from-transparent via-[var(--acc)] to-transparent" />

                  <div
                    className={`flex flex-col ${csImageUrl ? "lg:flex-row" : ""} gap-8 p-8 md:p-12`}
                  >
                    {/* Image */}
                    {csImageUrl && (
                      <div className="flex-shrink-0 lg:w-[40%]">
                        <div className="overflow-hidden rounded-2xl border border-white/10">
                          <img
                            src={csImageUrl}
                            alt={
                              section.imageAlt || section.title || "Case study"
                            }
                            className="h-auto w-full object-contain"
                          />
                        </div>
                      </div>
                    )}

                    {/* Content */}
                    <div className="flex flex-1 flex-col justify-center">
                      <div className="mb-4 inline-flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full bg-[var(--acc)]" />
                        <span className="text-xs font-bold uppercase tracking-widest text-[var(--acc)]">
                          Case Study
                        </span>
                      </div>

                      <h3 className="mb-5 font-serif text-2xl font-light text-white md:text-3xl">
                        {section.title}
                      </h3>

                      <p className="whitespace-pre-line text-base leading-relaxed text-gray-300 md:text-lg">
                        {section.description}
                      </p>

                      {section.ctaHref && (
                        <div className="mt-8">
                          <Link to={section.ctaHref}>
                            <Button variant="secondary" size="lg">
                              {section.ctaLabel || "Read Full Case Study"}
                              <svg
                                className="ml-2 inline-block h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                                />
                              </svg>
                            </Button>
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          );
        })()}

        {/* Products & Features Section */}
        {(() => {
          const section = getSection("productsFeaturesSection");
          if (!section?.features || section.features.length === 0) return null;
          return <ProductsFeaturesSection section={section} />;
        })()}

        {/* Features Grid Section - 4 Column Layout */}
        {(() => {
          const section = getSection("featuresGrid");
          if (!section?.items || section.items.length === 0) return null;
          return (
            <section style={{ padding: "6rem 0" }} className="px-4 md:px-12">
              {/* Header */}
              <div style={{ textAlign: "center", marginBottom: "4rem" }}>
                {section.heading && (
                  <h2
                    className="font-serif"
                    style={{
                      fontSize: "clamp(2.2rem, 3.5vw, 3rem)",
                      letterSpacing: "-0.02em",
                      lineHeight: 1.15,
                      color: "#ffffff",
                      marginBottom: "1rem",
                    }}
                  >
                    {section.heading}
                  </h2>
                )}
                {section.subheading && (
                  <p
                    style={{
                      fontSize: "1rem",
                      color: "rgba(255,255,255,0.7)",
                      lineHeight: 1.7,
                      fontWeight: 300,
                      maxWidth: "700px",
                      margin: "0 auto",
                    }}
                  >
                    {section.subheading}
                  </p>
                )}
              </div>

              {/* Features - 4 Column Grid */}
              <div
                style={{
                  maxWidth: "1300px",
                  margin: "0 auto",
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: "1.5rem",
                }}
                className="!grid-cols-1 sm:!grid-cols-2 lg:!grid-cols-4"
              >
                {section.items.map((item: any, i: number) => (
                  <div
                    key={i}
                    style={{
                      padding: "2rem 1.5rem",
                      background: "rgba(255, 255, 255, 0.02)",
                      border: "1px solid rgba(255, 255, 255, 0.06)",
                      borderRadius: "20px",
                      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                      display: "flex",
                      flexDirection: "column",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background =
                        "rgba(255, 255, 255, 0.04)";
                      e.currentTarget.style.borderColor = "var(--accBorder)";
                      e.currentTarget.style.boxShadow =
                        "0 8px 40px var(--accGlow)";
                      e.currentTarget.style.transform = "translateY(-4px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background =
                        "rgba(255, 255, 255, 0.02)";
                      e.currentTarget.style.borderColor =
                        "rgba(255, 255, 255, 0.06)";
                      e.currentTarget.style.boxShadow = "none";
                      e.currentTarget.style.transform = "translateY(0)";
                    }}
                  >
                    {/* Feature Number Badge */}
                    <div
                      style={{
                        width: "44px",
                        height: "44px",
                        borderRadius: "12px",
                        background: `linear-gradient(135deg, var(--accA), var(--accB))`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "1rem",
                        fontWeight: 700,
                        color: "#000000",
                        boxShadow: `0 4px 20px var(--accGlow)`,
                        marginBottom: "1.25rem",
                      }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </div>

                    {/* Media (if provided) */}
                    {item.media && (
                      <div
                        style={{
                          borderRadius: "14px",
                          overflow: "hidden",
                          border: "1px solid rgba(255, 255, 255, 0.1)",
                          background: "rgba(0, 0, 0, 0.3)",
                          marginBottom: "1.25rem",
                        }}
                      >
                        {item.media.kind === "audioFile" ? (
                          <MediaRender media={item.media} />
                        ) : (
                          <div style={{ aspectRatio: "16/10", width: "100%" }}>
                            <MediaRender
                              media={item.media}
                              className="h-full w-full"
                              style={{ objectFit: "cover" }}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Title */}
                    {item.title && (
                      <h3
                        className="font-serif"
                        style={{
                          fontSize: "1.2rem",
                          letterSpacing: "-0.02em",
                          color: "#ffffff",
                          lineHeight: 1.3,
                          marginBottom: "0.75rem",
                        }}
                      >
                        {item.title}
                      </h3>
                    )}

                    {/* Description */}
                    {item.description && (
                      <p
                        style={{
                          fontSize: "0.85rem",
                          color: "rgba(255,255,255,0.7)",
                          lineHeight: 1.7,
                          fontWeight: 300,
                          flex: 1,
                        }}
                      >
                        {item.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          );
        })()}

        {/* Bottom CTA Section */}
        {(() => {
          const section = getSection("useCaseBottomCta");
          if (!section) return null;
          return (
            <section
              style={solidCtaBg()}
              className="px-4 py-20 md:px-12 md:py-28"
            >
              <div className="mx-auto max-w-4xl text-center">
                <Title size="lg" className="mb-6">
                  {section.heading}
                </Title>
                {section.subheading && (
                  <Description className="mx-auto mb-10 max-w-2xl text-lg">
                    {section.subheading}
                  </Description>
                )}
                <div className="flex flex-wrap justify-center gap-4">
                  {section.primaryCta && (
                    <Link to={section.primaryCta.href || "#"}>
                      <Button variant="primary" size="lg">
                        {section.primaryCta.label}
                      </Button>
                    </Link>
                  )}
                  {section.secondaryCta && (
                    <Link to={section.secondaryCta.href || "#"}>
                      <Button variant="secondary" size="lg">
                        {section.secondaryCta.label}
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            </section>
          );
        })()}
      </div>
    </>
  );
};

export default UseCasePage;
